import { useEffect, useState } from "react";
import { api, API, getToken, fileUrl } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Video, Sparkles, Upload, Trash2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const STATUS_COLORS = {
  asignado: "pill-gray",
  en_produccion: "pill-yellow",
  en_revision: "pill-blue",
  requiere_cambios: "pill-red",
  aprobado: "pill-green",
  publicado: "pill-dark",
};

const COLUMNS = [
  { id: "asignado", label: "Asignados" },
  { id: "en_produccion", label: "En edición" },
  { id: "en_revision", label: "En revisión" },
  { id: "requiere_cambios", label: "Cambios" },
  { id: "aprobado", label: "Aprobados" },
  { id: "publicado", label: "Publicados" },
];

const VideoLab = () => {
  const [items, setItems] = useState([]);
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/command/video-edits"),
      api.get("/command/video-formats"),
    ]).then(([v, f]) => { setItems(v.data); setFormats(f.data); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDrop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    await api.patch(`/command/video-edits/${id}`, { status });
    load();
  };

  return (
    <Layout
      title="Video Lab"
      subtitle="24 formatos virales · briefs IA · entregables"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-video-btn">
          <Sparkles size={14} /> Asignar video
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <>
          {/* Format library */}
          <div className="aura-card-flat p-5 mb-5">
            <div className="label-overline mb-3">📚 Biblioteca de formatos disponibles ({formats.length})</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto" data-testid="format-library">
              {formats.map((f) => (
                <div key={f.id} className="text-xs p-2 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5]">
                  <div className="font-medium">{f.label}</div>
                  <div className="text-[10px] text-[#666] mt-0.5">{f.duration}</div>
                </div>
              ))}
            </div>
          </div>

          {items.length === 0 ? (
            <div className="aura-card-flat p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
                <Video size={26} />
              </div>
              <h3 className="font-display text-2xl font-medium">Lab vacío</h3>
              <p className="text-[#666] mt-2 mb-5">Asigna videos con formato específico para que el editor empiece a producir.</p>
              <button onClick={() => setShowForm(true)} className="btn-primary">Asignar primer video</button>
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="video-kanban">
              {COLUMNS.map((col) => {
                const colItems = items.filter((b) => b.status === col.id);
                return (
                  <div key={col.id} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, col.id)} className="kanban-col" data-testid={`video-col-${col.id}`}>
                    <div className="mb-3 px-1 flex justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#666]">{col.label}</span>
                      <span className="text-xs text-[#666]">{colItems.length}</span>
                    </div>
                    <div className="space-y-2">
                      {colItems.map((v) => (
                        <div
                          key={v.id}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData("text/plain", v.id)}
                          onClick={() => setSelected(v)}
                          className="kanban-card"
                          data-testid={`video-${v.id}`}
                        >
                          <div className="text-[10px] text-[#8a5e10] font-medium mb-1">{v.format_label}</div>
                          <div className="text-sm font-medium">{v.title}</div>
                          <div className="text-[10px] text-[#666] mt-1">{v.assignee_name || "sin asignar"}</div>
                          {v.versions?.length > 0 && (
                            <div className="text-[10px] text-[#666] mt-1">🎞 {v.versions.length} versión{v.versions.length > 1 ? "es" : ""}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showForm && <VideoForm formats={formats} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {selected && <VideoDetail video={selected} onClose={() => setSelected(null)} onChange={load} />}
    </Layout>
  );
};

const VideoForm = ({ formats, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: "",
    format: "",
    brief: "",
    campaign_id: "",
    assignee_id: "",
    assignee_name: "",
    duration: "",
    due_date: "",
    priority: "media",
  });
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/campaigns").then((r) => setCampaigns(r.data));
  }, []);

  const generateBrief = async () => {
    if (!form.format) { toast.error("Selecciona un formato primero"); return; }
    setGeneratingBrief(true);
    try {
      const camp = campaigns.find((c) => c.id === form.campaign_id);
      const r = await api.post("/command/video-edits/generate-brief", {
        format: form.format,
        title: form.title || "Video sin título",
        campaign_context: camp ? `${camp.name}: ${camp.objective}` : "",
        duration: form.duration,
      });
      setForm((f) => ({ ...f, brief: r.data.brief }));
      toast.success("Brief generado por Aura");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setGeneratingBrief(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.format) { toast.error("Título y formato son requeridos"); return; }
    setSaving(true);
    try {
      await api.post("/command/video-edits", form);
      toast.success("Video asignado al editor");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Video Lab</div>
            <h2 className="font-display text-2xl font-medium">Asignar nuevo video</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="aura-label">Título *</label>
            <input className="aura-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="POV: vendedora estrella recomendando perfumes" data-testid="video-title" />
          </div>
          <div className="col-span-2">
            <label className="aura-label">Formato * ({formats.length} disponibles)</label>
            <select className="aura-input" required value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="video-format">
              <option value="">Selecciona uno...</option>
              {formats.map((f) => <option key={f.id} value={f.id}>{f.label} — {f.desc} ({f.duration})</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Campaña</label>
            <select className="aura-input" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} data-testid="video-campaign">
              <option value="">Sin campaña</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Asignar a</label>
            <select className="aura-input" value={form.assignee_id} onChange={(e) => {
              const u = users.find((x) => x.id === e.target.value);
              setForm({ ...form, assignee_id: e.target.value, assignee_name: u?.name || "" });
            }} data-testid="video-assignee">
              <option value="">— Sin asignar —</option>
              {users.filter((u) => ["editor_video", "filmmaker", "filmmaker_ia", "creador_interno", "creador_ugc"].includes(u.role)).map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="aura-label">Duración</label>
            <input className="aura-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="20-30s" />
          </div>
          <div>
            <label className="aura-label">Entrega</label>
            <input type="date" className="aura-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="aura-label mb-0">Brief técnico para el editor</label>
            <button type="button" onClick={generateBrief} disabled={generatingBrief} className="btn-outline text-xs flex items-center gap-1" data-testid="generate-video-brief">
              {generatingBrief ? <span className="spinner" /> : <Sparkles size={11} />} {form.brief ? "Regenerar" : "Generar con Aura"}
            </button>
          </div>
          <textarea className="aura-input font-mono text-xs" rows="10" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="Aura puede generarte un brief profesional según el formato seleccionado..." />
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-2" data-testid="submit-video">
            {saving && <span className="spinner" />} Asignar video
          </button>
        </div>
      </form>
    </div>
  );
};

const VideoDetail = ({ video, onClose, onChange }) => {
  const [v, setV] = useState(video);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const fileRef = useState(null)[0];
  let fileInputRef;

  const refresh = async () => {
    const r = await api.get("/command/video-edits");
    const fresh = r.data.find((x) => x.id === v.id);
    if (fresh) setV(fresh);
  };

  const uploadVersion = async (f) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "video");
      fd.append("related_type", "video_edit");
      fd.append("related_id", v.id);
      const res = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      await api.post(`/command/video-edits/${v.id}/versions`, {
        file_id: data.id,
        file_url: fileUrl(data.id),
        notes,
      });
      setNotes("");
      await refresh();
      onChange();
      toast.success("Versión subida");
    } catch (err) {
      toast.error("Error al subir");
    } finally { setUploading(false); }
  };

  const updateStatus = async (s) => {
    await api.patch(`/command/video-edits/${v.id}`, { status: s });
    await refresh();
    onChange();
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/command/video-edits/${v.id}`);
    onChange();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">{v.format_label}</div>
            <h2 className="font-display text-2xl font-medium">{v.title}</h2>
            <p className="text-sm text-[#666] mt-1">{v.assignee_name || "sin asignar"}{v.duration ? ` · ${v.duration}` : ""}</p>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {Object.keys(STATUS_COLORS).map((s) => (
            <button key={s} onClick={() => updateStatus(s)} className={`pill cursor-pointer text-[10px] ${v.status === s ? STATUS_COLORS[s] : "pill-gray"}`} data-testid={`status-${s}`}>{s.replace(/_/g, " ")}</button>
          ))}
        </div>

        {v.brief && (
          <div className="aura-card-flat p-4 mb-4 max-h-72 overflow-y-auto">
            <div className="label-overline mb-2">Brief técnico</div>
            <Markdown content={v.brief} />
          </div>
        )}

        {/* Versions */}
        <div className="border-t border-[#E5E5E5] pt-4 mb-4">
          <div className="label-overline mb-3">Versiones ({(v.versions || []).length})</div>
          {(v.versions || []).length === 0 ? (
            <p className="text-xs text-[#666]">Aún no hay entregas. Sube la primera versión low-res para revisión.</p>
          ) : (
            <div className="space-y-2">
              {v.versions.map((ver, i) => (
                <div key={ver.id || i} className="flex justify-between items-center text-sm bg-[#FAFAFA] rounded-lg p-3">
                  <div>
                    <div className="font-medium">v{i + 1} · {new Date(ver.uploaded_at).toLocaleString("es-MX")}</div>
                    {ver.notes && <div className="text-xs text-[#666]">{ver.notes}</div>}
                  </div>
                  {ver.file_url && (
                    <a href={ver.file_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs">Ver</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload */}
        <div className="border-t border-[#E5E5E5] pt-4">
          <input type="file" accept="video/*,image/*" ref={(r) => (fileInputRef = r)} className="hidden" onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadVersion(f);
            e.target.value = "";
          }} />
          <textarea className="aura-input text-xs mb-2" rows="2" placeholder="Notas para esta entrega..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-between gap-2">
            <button onClick={remove} className="btn-ghost text-red-600 text-xs"><Trash2 size={12} className="inline mr-1" /> Eliminar</button>
            <button onClick={() => fileInputRef?.click()} disabled={uploading} className="btn-primary text-xs flex items-center gap-1" data-testid="upload-video-version">
              {uploading ? <span className="spinner" /> : <Upload size={12} />} Subir nueva versión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoLab;
